import React from 'react'
import { Button } from '../common/Button'
import { Modal } from '../modals/CurieModal'
import { Typography } from '../common/Typography'

export interface AlertModalProps {
  open: boolean
  onClose: () => void
  title: string
  message: string
  /**
   * Optional primary action button configuration
   * If not provided, only the cancel/close button will be shown
   */
  primaryAction?: {
    label: string
    onClick: () => void
    variant?: 'primary' | 'outlined' | 'borderless' | 'destructive'
  }
  /**
   * Optional secondary action button configuration
   */
  secondaryAction?: {
    label: string
    onClick: () => void
    variant?: 'primary' | 'outlined' | 'borderless' | 'destructive'
  }
  /**
   * Label for the close/dismiss button
   * @default "Close"
   */
  cancelLabel?: string
  /**
   * Custom icon component (optional)
   * If not provided, default info icon will be used
   */
  icon?: React.ReactNode
}

export function AlertModal({
  open,
  onClose,
  title,
  message,
  primaryAction,
  secondaryAction,
  cancelLabel = 'Close',
  icon
}: AlertModalProps) {
  return (
    <Modal isOpen={open} onClose={onClose}>
      <div className='flex gap-3 px-6 py-8'>
        <div className='flex items-start gap-3'>{icon || <InfoIcon className='h-6 w-6' />}</div>
        <div className='flex flex-col justify-end items-start w-full gap-8'>
          <Typography
            fontFamily='geist'
            fontSize='titleLg'
            fontWeight='bold'
            className='text-black'
          >
            {title}
          </Typography>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            fontWeight='normal'
            className='text-black'
          >
            {message}
          </Typography>
          <div className='flex gap-3 justify-end w-full h-8 mt-4'>
            <Button variant='outlined' onClick={onClose}>
              {cancelLabel}
            </Button>
            {secondaryAction && (
              <Button
                variant={secondaryAction.variant || 'outlined'}
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            )}
            {primaryAction && (
              <Button variant={primaryAction.variant || 'primary'} onClick={primaryAction.onClick}>
                {primaryAction.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

function InfoIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      fill='currentColor'
      className={'text-indigo-600 ' + className}
      aria-hidden='true'
    >
      <path d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm.75 14.5h-1.5v-6h1.5v6Zm0-8.5h-1.5V6h1.5v2Z' />
    </svg>
  )
}
